import { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getViews, createView, updateView, deleteView, setDefaultView, getActivities } from '../services/api';

export const ViewsManager = () => {
  const { t, language } = useLanguage();
  const [views, setViews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [formData, setFormData] = useState({
    view_name: '',
    filter_criteria: { activity: '' },
    sort_criteria: { created_at: 'desc' },
  });
  const toast = useToast();

  useEffect(() => {
    fetchViews();
    fetchActivities();
  }, []);

  const fetchViews = async () => {
    try {
      setLoading(true);
      const response = await getViews();
      setViews(response.data || []);
    } catch (error) {
      toast.error(t('failedToLoadViews'));
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const data = await getActivities(true); // Only active activities
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error(t('failedToLoadActivities'));
    }
  };

  const handleOpenModal = (view = null) => {
    if (view) {
      setEditingView(view);
      setFormData({
        view_name: view.view_name,
        filter_criteria: view.filter_criteria || { activity: '' },
        sort_criteria: view.sort_criteria || { created_at: 'desc' },
      });
    } else {
      setEditingView(null);
      setFormData({
        view_name: '',
        filter_criteria: { activity: '' },
        sort_criteria: { created_at: 'desc' },
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingView(null);
    setFormData({
      view_name: '',
      filter_criteria: { activity: '' },
      sort_criteria: { created_at: 'desc' },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingView) {
        await updateView(editingView.id, formData);
        toast.success(t('viewUpdatedSuccessfully'));
      } else {
        await createView(formData);
        toast.success(t('viewCreatedSuccessfully'));
      }
      handleCloseModal();
      fetchViews();
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to save view';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t('confirmDeleteView'))) {
      return;
    }

    try {
      await deleteView(id);
      toast.success(t('viewDeletedSuccessfully'));
      fetchViews();
    } catch (error) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to delete view';
      toast.error(errorMsg);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultView(id);
      toast.success(t('defaultViewUpdated'));
      fetchViews();
    } catch (error) {
      toast.error(t('failedToSetDefaultView'));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleActivityChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      filter_criteria: {
        ...prev.filter_criteria,
        activity: e.target.value,
      },
    }));
  };

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('filteredViews')}</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {t('filteredViewsDescription')}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto">
          <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M12 4v16m8-8H4" />
          </svg>
          {t('addView')}
        </Button>
      </div>

      {views.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 sm:p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">{t('noViewsYet')}</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{t('createFirstViewDescription')}</p>
          <Button onClick={() => handleOpenModal()}>{t('createFirstView')}</Button>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {views.map((view) => (
              <div key={view.id} className="bg-white shadow-md rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{view.view_name}</h3>
                    <div className="mt-2">
                      {view.filter_criteria?.activity ? (
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                          {view.filter_criteria.activity}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">{t('all')}</span>
                      )}
                    </div>
                  </div>
                  {view.is_default && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 whitespace-nowrap">
                      {t('default')}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  {!view.is_default && (
                    <button
                      onClick={() => handleSetDefault(view.id)}
                      className="text-sm text-blue-600 hover:text-blue-900 font-medium"
                    >
                      {t('setAsDefault')}
                    </button>
                  )}
                  <div className={`flex items-center gap-2 ${view.is_default ? 'ml-auto' : ''}`}>
                    <button
                      onClick={() => handleOpenModal(view)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('edit') || 'Edit'}
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(view.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('delete') || 'Delete'}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    {t('viewName')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    {t('activityFilter')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    {t('default')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {views.map((view) => (
                  <tr key={view.id}>
                    <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm font-medium text-gray-900">
                        {view.view_name}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-600">
                        {view.filter_criteria?.activity ? (
                          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                            {view.filter_criteria.activity}
                          </span>
                        ) : (
                          <span className="text-gray-400">{t('all')}</span>
                        )}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                      {view.is_default ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {t('default')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetDefault(view.id)}
                          className="text-sm text-blue-600 hover:text-blue-900"
                        >
                          {t('setAsDefault')}
                        </button>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center gap-2 justify-start">
                        <button
                          onClick={() => handleOpenModal(view)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('edit') || 'Edit'}
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(view.id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('delete') || 'Delete'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingView ? t('editView') : t('createView')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('viewName')}
            name="view_name"
            value={formData.view_name}
            onChange={handleChange}
            placeholder={t('placeholderViewName') || 'e.g., Adults View, Academy View'}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('filterByActivity')}
            </label>
            <select
              value={formData.filter_criteria?.activity || ''}
              onChange={handleActivityChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('allActivities')}</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.activity_name}>
                  {activity.activity_name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t('filterByActivityHelp')}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingView ? t('updateView') : t('createView')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCloseModal} className="flex-1">
              {t('cancel')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
